import { Alert } from '@/shared/components/ui';
import { useState } from 'react';
import styles from './AlertsPage.module.css';

export const AlertsPage = () => {
    const [showInfo, setShowInfo] = useState(true);
    const [showSuccess, setShowSuccess] = useState(true);
    const [showWarning, setShowWarning] = useState(true);
    const [showError, setShowError] = useState(true);

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h1>Alerts</h1>
                <p className={styles.description}>
                    Componente Alert para mostrar mensajes informativos, de éxito, advertencia o error.
                    Soporta títulos, contenido y opción para ser dismissible.
                </p>
            </div>

            {/* Tipos de Alert */}
            <section className={styles.section}>
                <h2>Tipos de Alert</h2>
                <p>Alert soporta 4 tipos visuales diferentes.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Alert type="info">
                            Este es un mensaje informativo. Úsalo para compartir información neutral.
                        </Alert>
                        <code className={styles.code}>
                            {`<Alert type="info">
  Este es un mensaje informativo...
</Alert>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Alert type="success">
                            ¡Operación completada exitosamente! Tus cambios han sido guardados.
                        </Alert>
                        <code className={styles.code}>
                            {`<Alert type="success">
  ¡Operación completada exitosamente!
</Alert>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Alert type="warning">
                            Advertencia: Esta acción no puede deshacerse. Procede con precaución.
                        </Alert>
                        <code className={styles.code}>
                            {`<Alert type="warning">
  Advertencia: Esta acción no puede deshacerse...
</Alert>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Alert type="error">
                            Error: No se pudo completar la operación. Por favor, intenta nuevamente.
                        </Alert>
                        <code className={styles.code}>
                            {`<Alert type="error">
  Error: No se pudo completar la operación...
</Alert>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Con Título */}
            <section className={styles.section}>
                <h2>Alert con Título</h2>
                <p>Agrega un título para destacar el mensaje principal.</p>

                <div className={styles.showcase}>
                    <div className={styles.example}>
                        <Alert type="info" title="Información Importante">
                            Revisa los términos y condiciones antes de continuar con el proceso.
                        </Alert>
                        <code className={styles.code}>
                            {`<Alert type="info" title="Información Importante">
  Revisa los términos y condiciones...
</Alert>`}
                        </code>
                    </div>

                    <div className={styles.example}>
                        <Alert type="success" title="¡Registro Exitoso!">
                            Tu cuenta ha sido creada. Revisa tu email para verificarla.
                        </Alert>
                        <code className={styles.code}>
                            {`<Alert type="success" title="¡Registro Exitoso!">
  Tu cuenta ha sido creada...
</Alert>`}
                        </code>
                    </div>
                </div>
            </section>

            {/* Dismissible */}
            <section className={styles.section}>
                <h2>Alert Dismissible</h2>
                <p>Los alerts pueden cerrarse con un botón de dismiss.</p>

                <div className={styles.showcase}>
                    {showInfo && (
                        <div className={styles.example}>
                            <Alert type="info" dismissible onDismiss={() => setShowInfo(false)}>
                                Este alert puede cerrarse. Haz click en la X para cerrarlo.
                            </Alert>
                            <code className={styles.code}>
                                {`<Alert 
  type="info"
  dismissible 
  onDismiss={() => setShow(false)}
>
  Este alert puede cerrarse...
</Alert>`}
                            </code>
                        </div>
                    )}

                    {showSuccess && (
                        <div className={styles.example}>
                            <Alert type="success" dismissible onDismiss={() => setShowSuccess(false)}>
                                ¡Listo! Puedes cerrar este mensaje cuando quieras.
                            </Alert>
                        </div>
                    )}

                    {showWarning && (
                        <div className={styles.example}>
                            <Alert type="warning" dismissible onDismiss={() => setShowWarning(false)}>
                                Ten cuidado. Este mensaje es importante pero puedes cerrarlo.
                            </Alert>
                        </div>
                    )}

                    {showError && (
                        <div className={styles.example}>
                            <Alert type="error" dismissible onDismiss={() => setShowError(false)}>
                                Algo salió mal,pero puedes cerrar este mensaje.
                            </Alert>
                        </div>
                    )}

                    {!showInfo && !showSuccess && !showWarning && !showError && (
                        <div className={styles.resetContainer}>
                            <p style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                                Todos los alerts han sido cerrados.
                            </p>
                            <button
                                className={styles.resetButton}
                                onClick={() => {
                                    setShowInfo(true);
                                    setShowSuccess(true);
                                    setShowWarning(true);
                                    setShowError(true);
                                }}
                            >
                                Mostrar Todos
                            </button>
                        </div>
                    )}
                </div>
            </section>

            {/* Ejemplos de Uso */}
            <section className={styles.section}>
                <h2>Ejemplos de Uso</h2>

                <div className={styles.exampleGroup}>
                    <h3>Validación de Formulario</h3>
                    <Alert type="error" title="Error de Validación">
                        Por favor corrige los siguientes errores antes de continuar:
                        <ul style={{ marginTop: '0.5rem', marginBottom: 0, paddingLeft: '1.5rem' }}>
                            <li>El email es requerido</li>
                            <li>La contraseña debe tener al menos 8 caracteres</li>
                        </ul>
                    </Alert>
                    <code className={styles.code}>
                        {`<Alert type="error" title="Error de Validación">
  Por favor corrige los siguientes errores...
  <ul>
    <li>El email es requerido</li>
    <li>La contraseña debe tener al menos 8 caracteres</li>
  </ul>
</Alert>`}
                    </code>
                </div>

                <div className={styles.exampleGroup}>
                    <h3>Confirmación de Operación</h3>
                    <Alert type="success" title="Datos Actualizados">
                        Los cambios han sido guardados correctamente.
                    </Alert>
                    <code className={styles.code}>
                        {`<Alert type="success" title="Datos Actualizados">
  Los cambios han sido guardados correctamente.
</Alert>`}
                    </code>
                </div>

                <div className={styles.exampleGroup}>
                    <h3>Información del Sistema</h3>
                    <Alert type="info" title="Mantenimiento Programado">
                        El sistema estará en mantenimiento el próximo sábado de 2:00 AM a 6:00 AM.
                    </Alert>
                    <code className={styles.code}>
                        {`<Alert type="info" title="Mantenimiento Programado">
  El sistema estará en mantenimiento...
</Alert>`}
                    </code>
                </div>
            </section>

            {/* Props */}
            <section className={styles.section}>
                <h2>Props</h2>
                <div className={styles.propsTable}>
                    <table>
                        <thead>
                            <tr>
                                <th>Prop</th>
                                <th>Tipo</th>
                                <th>Default</th>
                                <th>Descripción</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td><code>type</code></td>
                                <td><code>'info' | 'success' | 'warning' | 'error'</code></td>
                                <td><code>'info'</code></td>
                                <td>Tipo visual del alert</td>
                            </tr>
                            <tr>
                                <td><code>title</code></td>
                                <td><code>string</code></td>
                                <td>-</td>
                                <td>Título opcional del alert</td>
                            </tr>
                            <tr>
                                <td><code>children</code></td>
                                <td><code>ReactNode</code></td>
                                <td>-</td>
                                <td>Contenido del alert (requerido)</td>
                            </tr>
                            <tr>
                                <td><code>dismissible</code></td>
                                <td><code>boolean</code></td>
                                <td><code>false</code></td>
                                <td>Si muestra botón para cerrar</td>
                            </tr>
                            <tr>
                                <td><code>onDismiss</code></td>
                                <td><code>() =&gt; void</code></td>
                                <td>-</td>
                                <td>Función llamada al cerrar</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </section>

            {/* Import */}
            <section className={styles.section}>
                <h2>Importación</h2>
                <code className={styles.code}>
                    {`import { Alert } from '@/shared/components/ui';`}
                </code>
            </section>
        </div>
    );
};
